import { api } from '../api/client';

import { API_CHANGE_CARD_APPLY_URL, API_CHANGE_CARD_DRY_RUN_URL, API_CHANGE_CARD_TASK_UNDO_URL, API_CHANGE_CARD_TASK_URL } from '../utils';

/**
 * CHANGE CARD (KPE-028; keepr-api SCHEMA.md § Change card). Moving items onto
 * another card of the same collection family by mapping each target element
 * to what fills it.
 *
 * - `POST /api/collections/{id}/change-card/dry-run` (KPR-700) — the body is
 *   `{ card, target, ids | q | all, fills, unmapped?, rules? }`; the answer is
 *   every count the person must see (`items`, `sources`, `targets`,
 *   `keeprWrites`, `unmapped`, `warnings`, `preview`, `rules`, `dependents`)
 *   and a confirm token. It changes nothing. Refusals keep
 *   `error.response.data`: 400 `change_card_mapping_invalid` carries
 *   `problems: [{ element, part, code, message }]`, each naming a TARGET
 *   element; 409 `change_card_blocked_by_lookups`, the 403s and the 503
 *   `query_timeout` carry their own sentence.
 */
export async function changeCardDryRun(collectionId, body, { signal } = {}) {
	const { data } = await api.post(API_CHANGE_CARD_DRY_RUN_URL(collectionId), body, { signal });
	return data;
}

/**
 * The move (KPR-702): the dry run's body exactly as it was sent, plus
 * `confirm`, its token. What is applied is the token's. 200 `{ applied:
 * true, items, moved: [{ id, historyId }], failures, leftBehind, rules,
 * warnings }` for a change of up to 100 items; 202 `{ applied: false, task,
 * … }` for a whole card or more (KPR-705). `attestation` rides as
 * X-Attestation when a card asks for a signature on every change (428
 * `attestation_required` without it). Refusals keep `error.response.data`.
 */
export async function changeCardApply(collectionId, body, token, { attestation } = {}) {
	const headers = attestation ? { 'X-Attestation': attestation } : undefined;
	const { data } = await api.post(API_CHANGE_CARD_APPLY_URL(collectionId), { ...body, confirm: token }, headers ? { headers } : undefined);
	return data;
}

/**
 * A card change running as a background task (KPR-705; keepr-api SCHEMA.md §
 * Change card, "The background task"): a whole card, or more than 100 items.
 *
 *   { task: { id, status, running, progress: { total, done, moved, failed, skipped, skippedBy },
 *             card, target, startedAt, updatedAt, finishedAt, error? },
 *     by, items: { requested, done, moved, failed, skipped: { <reason>: n }, notReached },
 *     failures: [{ id, element?, code, message? }], failuresKept,
 *     list?: { outcome, items: [{ id, reason? }], next } }
 *
 * The person who started it reads it all; a manager of the collection reads
 * the failures without their elements and sentences. `outcome` (moved,
 * skipped or failed) with `after` and `limit` lists one page of its items.
 */
export async function getChangeCardTask(collectionId, taskId, { outcome, after, limit, signal } = {}) {
	const params = {};
	if (outcome) params.outcome = outcome;
	if (after) params.after = after;
	if (limit) params.limit = limit;
	const { data } = await api.get(API_CHANGE_CARD_TASK_URL(collectionId, taskId), { params, signal });
	return data;
}

/**
 * Undo a finished background card change, item by item (KPR-704; keepr-api
 * SCHEMA.md § Change card, "Undoing a background change"). Session only; the
 * person who started the change or a manager. 202 `{ undo }` — a task of its
 * own, `{ id, status, running, progress: { total, done, reverted, failed },
 * startedAt, updatedAt, finishedAt, error? }`. Refusals keep
 * `error.response.data` (409 change_card_task_running, change_card_task_undone,
 * change_card_nothing_to_undo; 428 attestation_required).
 */
export async function undoChangeCardTask(collectionId, taskId) {
	const { data } = await api.post(API_CHANGE_CARD_TASK_UNDO_URL(collectionId, taskId));
	return data;
}

/**
 * The latest undo of a background card change: `{ undo | null, undoable,
 * why?, failures: [{ id, code, message? }] }` — the sentences to whoever
 * started the undo.
 */
export async function getChangeCardUndo(collectionId, taskId, { signal } = {}) {
	const { data } = await api.get(API_CHANGE_CARD_TASK_UNDO_URL(collectionId, taskId), { signal });
	return data;
}
